import React, { useState, useRef } from "react";
import { useLocation } from "react-router-dom";

const SurveyForm = () => {
  const [reloadButton, setReloadbutton] = useState(false);
  const location = useLocation();
  const hideLayout = location.pathname === "/surveylink";
  const initialFormData = {
    email: "",
    priorityOne: "",
    priorityFour: [],
    challenges: "",
    opportunities: "",
    name: "",
    district: "",
    ageGroup: "",
    gender: "",
    whatsapp: "",
    movement: "",
  };

  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const refs = {
    email: useRef(null),
    priorityOne: useRef(null),
    priorityFour: useRef(null),
    challenges: useRef(null),
    opportunities: useRef(null),
    name: useRef(null),
    district: useRef(null),
    ageGroup: useRef(null),
    gender: useRef(null),
    whatsapp: useRef(null),
    movement: useRef(null),
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // ✅ clear error for this field
    setErrors((prev) => {
      if (!prev[name]) return prev;
      const copy = { ...prev };
      delete copy[name];
      return copy;
    });
  };

  const handleCheckbox = (field, value) => {
    setFormData((prev) => {
      const list = prev[field] || [];
      const updated = list.includes(value)
        ? list.filter((v) => v !== value)
        : [...list, value];

      return { ...prev, [field]: updated };
    });

    // ✅ clear error once at least one is selected
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const copy = { ...prev };
      delete copy[field];
      return copy;
    });
  };

  const validate = () => {
    const e = {};
    if (!formData.email) e.email = true;
    if (!formData.priorityOne) e.priorityOne = true;
    if (!formData.priorityFour.length) e.priorityFour = true;
    if (!formData.challenges) e.challenges = true;
    if (!formData.opportunities) e.opportunities = true;
    if (!formData.name) e.name = true;
    if (!formData.district.length) e.district = true;
    if (!formData.ageGroup.length) e.ageGroup = true;
    if (!formData.gender) e.gender = true;
    if (!formData.whatsapp) e.whatsapp = true;
    if (!formData.movement) e.movement = true;

    setErrors(e);

    if (Object.keys(e).length) {
      refs[Object.keys(e)[0]].current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const payload = {
      email: formData.email,
      top_priority: formData.priorityOne,
      other_priorities: formData.priorityFour,
      challenge_faced: formData.challenges,
      greatest_opportunity: formData.opportunities,
      name: formData.name,
      district: formData.district,
      age_group: formData.ageGroup,
      gender: formData.gender,
      whatsapp_number: formData.whatsapp,
      join_movement: formData.movement,
    };

    try {
      const response = await fetch(
        "https://api-discloseai.essolutions.ai/api/v1/documents/take-survey",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) throw new Error("Failed to submit complaint");

      if (response.status === 200) {
        resetSurveyForm();
        setReloadbutton(true);

        document.querySelector(".modal-body")?.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }
    } catch (err) {
      console.error("Submission error:", err);
    }

    console.log("SUBMIT DATA →", payload);
  };

  const resetSurveyForm = () => {
    setFormData(initialFormData);
    setErrors({});
  };

  const handleCopy = async () => {
    const urlToCopy = "https://www.seas2050.org/surveylink"; // 👈 replace with your URL

    try {
      await navigator.clipboard.writeText(urlToCopy);
      alert("Link copied to clipboard!");
    } catch (err) {
      console.error("Failed to copy: ", err);
    }
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <>
      <div className="modal fade show d-block survey-modal" tabIndex="-1">
        <div
          className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable"
          style={{ paddingTop: !hideLayout ? "63px" : "19px" }}
        >
          <div className="modal-content rounded-4 shadow-lg">
            {/* HEADER */}
            <div className="modal-header border-0 pb-2">
              <div>
                <h4 className="fw-bold mb-1" style={{ color: "#1155cc" }}>
                  SEAS FOR 2050 – A SUSTAINABLE DEVELOPMENT PLAN FOR SIERRA
                  LEONE IN 2050
                </h4>
                <p className="fw-bold text-muted mb-0">
                  OUR OPENING QUESTIONNAIRE
                </p>
                {!hideLayout && (
                  <button
                    variant="primary"
                    onClick={handleCopy}
                    className="px-4 py-2 fw-semibold"
                    style={{
                      borderRadius: "8px",
                      backgroundColor: "#1155cc",
                      border: "none",
                    }}
                  >
                    Copy Survey Link
                  </button>
                )}
              </div>
            </div>

            {/* BODY */}
            <div className="modal-body pt-3">
              {/* INTRO */}
              {reloadButton && hideLayout ? (
                <div className="intro-box mb-4 fade-slide">
                  <p className="text-muted mb-0">
                    Your response has been recorded
                  </p>
                  <p
                    className="mb-0"
                    onClick={handleRefresh}
                    style={{
                      color: "blue",
                      textDecoration: "underline",
                      cursor: "pointer",
                    }}
                  >
                    Submit another response
                  </p>
                </div>
              ) : (
                <div className="intro-box mb-4 fade-slide">
                  <p className="text-muted mb-0">
                    There are no wrong or right answers and you should simply
                    answer with your first thoughts, rather than try and
                    best-guess the outcome. In this opening phase, we simply
                    need to understand what all our stakeholders think are the
                    most important challenges for the future of Salone and their
                    own lives. We also need to know where you are living and a
                    few details about age, gender and education. All information
                    will be held as strictly confidential by our media team
                    which is signed up to international standards of data
                    protection.
                  </p>
                </div>
              )}

              <form onSubmit={handleSubmit}>
                {/* EMAIL */}
                <div ref={refs.email} className="question-row fade-slide">
                  <label className="fw-bold mb-2 d-block">Email</label>
                  <input
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`form-control ${errors.email && "is-invalid"}`}
                  />
                </div>

                {/* PRIORITY 1 */}
                <div className="question-row fade-slide">
                  <p className="fw-bold mb-1">
                    If you can choose only one priority for action from the list
                    below, which one will it be?
                  </p>
                  <p className="text-muted mb-3">
                    My top priority for the next 25 years is:
                  </p>

                  <div ref={refs.priorityOne} className="row g-2">
                    {[
                      "Provide top class education for everyone",
                      "Ensure every household has access to safe drinking water",
                      "Create a reliable electricity supply to all households",
                      "Ensure everyone has enough food so no one goes hungry",
                      "Provide good medical facilities in every community",
                      "Upgrade the roads in my area",
                      "Develop better internet connections and telecom services",
                      "Introduce more job opportunities for the youth",
                    ].map((o, index) => (
                      <div className="col-md-6" key={index}>
                        <label key={o} className="survey-choice">
                          <input
                            type="radio"
                            name="priorityOne"
                            style={{ marginRight: "revert" }}
                            value={o}
                            checked={formData.priorityOne === o}
                            onChange={handleChange}
                          />
                          {o}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* PRIORITY 4 */}
                <div
                  ref={refs.priorityFour}
                  className="question-row fade-slide"
                >
                  <p className="fw-bold mb-3">
                    If you can choose another four priorities for action from
                    the list above what would they be?
                  </p>

                  <div className="row g-2">
                    {[
                      "Provide top class education for everyone",
                      "Ensure every household has access to safe drinking water",
                      "Provide good medical facilities in every community",
                      "Ensure everyone has enough food so no one goes hungry",
                      "Upgrade the roads in my area",
                      "Develop better internet connections and telecom services",
                      "Introduce more job opportunities for the youth",
                      "Create a reliable electricity supply to all households",
                    ].map((o, index) => (
                      <div className="col-md-6" key={index}>
                        <label key={o} className="survey-choice">
                          <input
                            type="checkbox"
                            style={{ marginRight: "revert" }}
                            onChange={() => handleCheckbox("priorityFour", o)}
                            checked={formData.priorityFour.includes(o)}
                          />
                          {o}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* CHALLENGES */}
                <div ref={refs.challenges} className="question-row fade-slide">
                  <label className="fw-bold mb-2 d-block">
                    What are the most important challenges facing Sierra Leone
                    over the next 25 years?
                  </label>
                  <input
                    name="challenges"
                    value={formData.challenges}
                    onChange={handleChange}
                    className={`form-control ${
                      errors.challenges && "is-invalid"
                    }`}
                  />
                </div>

                {/* OPPORTUNITIES */}
                <div
                  ref={refs.opportunities}
                  className="question-row fade-slide"
                >
                  <label className="fw-bold mb-2 d-block">
                    What are the greatest opportunities for the Country over the
                    next 25 years?
                  </label>
                  <input
                    name="opportunities"
                    value={formData.opportunities}
                    onChange={handleChange}
                    className={`form-control ${
                      errors.opportunities && "is-invalid"
                    }`}
                  />
                </div>

                {/* Name   */}
                <div ref={refs.name} className="question-row fade-slide">
                  <label className="fw-bold mb-2 d-block">
                    What is your name?
                  </label>
                  <input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`form-control ${errors.name && "is-invalid"}`}
                  />
                </div>

                {/* District 5 */}
                <div ref={refs.district} className="question-row fade-slide">
                  <p className="fw-bold mb-3">Which District do you live in?</p>

                  <div className="row g-2">
                    {[
                      "Western Area Urban",
                      "Western Area Rural",
                      "Port Loko",
                      "Kambia",
                      "Bombali",
                      "Tonkolili",
                      "Koinadugu",
                      "Falaba",
                      "Bo",
                      "Moyamba",
                      "Bonthe",
                      "Pujehun",
                      "Kenema",
                      "Kailahun",
                      "Kono",
                      "Makeni",
                    ].map((d, index) => (
                      <div className="col-md-6" key={index}>
                        <label key={d} className="survey-choice">
                          <input
                            type="radio"
                            name="district"
                            style={{ marginRight: "revert" }}
                            value={d}
                            onChange={handleChange}
                            checked={formData.district === d}
                          />
                          {d}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* age 6 */}
                <div ref={refs.ageGroup} className="question-row fade-slide">
                  <p className="fw-bold mb-3">
                    What Age group do you fall into?
                  </p>

                  <div className="row g-2">
                    {[
                      "0-10",
                      "11-20",
                      "21-30",
                      "31-40",
                      "41-50",
                      "51-60",
                      "61-70",
                      "Over 71",
                    ].map((a, index) => (
                      <div className="col-md-6" key={index}>
                        <label key={a} className="survey-choice">
                          <input
                            type="radio"
                            name="ageGroup"
                            style={{ marginRight: "revert" }}
                            value={a}
                            checked={formData.ageGroup === a}
                            onChange={handleChange}
                          />
                          {a}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Gender 7 */}
                <div ref={refs.gender} className="question-row fade-slide">
                  <p className="fw-bold mb-3">Gender</p>

                  <div className="row g-2">
                    {["Male", "Female"].map((g, index) => (
                      <div className="col-md-6" key={index}>
                        <label key={g} className="survey-choice">
                          <input
                            type="radio"
                            name="gender"
                            style={{ marginRight: "revert" }}
                            value={g}
                            checked={formData.gender === g}
                            onChange={handleChange}
                          />
                          {g}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Number 8 */}
                <div ref={refs.whatsapp} className="question-row fade-slide">
                  <label className="fw-bold mb-2 d-block">
                    If yes, your WhatsApp Number
                  </label>
                  <input
                    name="whatsapp"
                    value={formData.whatsapp}
                    onChange={handleChange}
                    className={`form-control ${
                      errors.whatsapp && "is-invalid"
                    }`}
                  />
                </div>

                {/* movement 8 */}
                <div ref={refs.movement} className="question-row fade-slide">
                  <p className="fw-bold mb-3">
                    Will you like to join the movement?
                  </p>

                  <div className="row g-2">
                    {["Yes", "No"].map((m, index) => (
                      <div className="col-md-6" key={index}>
                        <label key={m} className="survey-choice">
                          <input
                            type="radio"
                            name="movement"
                            style={{ marginRight: "revert" }}
                            value={m}
                            checked={formData.movement === m}
                            onChange={handleChange}
                          />
                          {m}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="modal-footer border-0 pt-2">
                  <button type="submit" className="btn btn-primary px-4">
                    Submit
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      <div className="modal-backdrop fade show" />
    </>
  );
};

export default SurveyForm;
